/**
 * The top bar's link to the guide page about the screen in front of you.
 *
 * Read off the routes the router matched, like the breadcrumb: each route names
 * its page in a `handle`, and the deepest one that names any wins, so a section
 * inherits its surface's page until it has one of its own.
 *
 * @module components/shell/RouteGuideLink
 */

import React from "react";

import { useMatches } from "react-router";

import { ExternalLinkButton } from "@/components/ui";
import { useOptionalWorkspaceContext } from "@/router/workspace-context";

import {
  type GuidePageId,
  guidePageUrl,
  guidePageVisible,
  type RouteGuideHandle,
} from "./guide-links";

/** The guide page the deepest of these matches names, if any does. */
export function deepestGuidePage(
  matches: readonly { handle?: unknown }[],
): GuidePageId | undefined {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const guide = (matches[index]!.handle as RouteGuideHandle | undefined)?.guide;
    if (guide) {
      return guide;
    }
  }
  return undefined;
}

/** The guide page the deepest matched route names, if any does. */
export function useRouteGuidePage(): GuidePageId | undefined {
  return deepestGuidePage(useMatches());
}

export function RouteGuideLink(): React.ReactElement | null {
  const page = useRouteGuidePage();
  const workspace = useOptionalWorkspaceContext();
  // No link rather than one the guide would answer with a 404.
  if (!page || !guidePageVisible(page, workspace?.canManageTenant ?? false)) {
    return null;
  }
  return (
    <ExternalLinkButton
      href={guidePageUrl(page)}
      variant="ghost"
      size="sm"
      icon="question"
      data-testid="route-guide-link"
    >
      Help
    </ExternalLinkButton>
  );
}
